import React, { useEffect, useState, useCallback } from 'react';
import { Layers, Plus, Save, AlertTriangle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import ApiService from '../../../../services/api.service';
import { reorder, remove, addOne, update, validate } from '../../helpers/levels.helpers';
import LevelsList from './components/LevelsList';

function LevelsPageV3() {
  const { department } = useParams();

  const [levels, setLevels] = useState([]);
  const [initialLevels, setInitialLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadLevels = useCallback(() => {
    ApiService.head.getDepartmentLevels(department).then((data) => {
      setLevels(data);
      setInitialLevels(data);
      setIsLoading(false);
    });
  }, [department]);

  useEffect(() => {
    if (isLoading) {
      loadLevels();
    }
  }, [isLoading, loadLevels]);

  const handleDragEnd = ({ destination, source }) => {
    if (!destination) return;

    const items = reorder({
      levels,
      start: source.index,
      end: destination.index,
    });

    setLevels(items);
  };

  const handleRemoveLevel = (index) => {
    const items = remove(levels, index);
    setLevels(items);
  };

  const handleAddLevel = () => {
    const items = addOne(levels);
    setLevels(items);
  };

  const handleInput = (title, index) => {
    if (title) {
      setHasError(false);
    }

    const items = update({
      levels,
      title,
      index,
    });

    setLevels(items);
  };

  const handleSave = () => {
    const validationError = validate(levels);

    if (validationError) {
      setError(validationError);
      setTimeout(() => setError(''), 3000);
      return;
    }

    ApiService.head
      .updateDepartmentLevels(department, levels)
      .then(() => {
        setMessage('Levels saved successfully!');
        setTimeout(() => setMessage(''), 3000);
        setIsLoading(true);
      })
      .catch(({ response }) => {
        setError(response?.data?.messages || 'Failed to save levels');
        setTimeout(() => setError(''), 3000);
      });
  };

  const hasChanges =
    JSON.stringify(levels.map((l) => ({ key: l.key, title: l.title }))) !==
    JSON.stringify(initialLevels.map((l) => ({ key: l.key, title: l.title })));

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Layers className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Levels</h1>
              <p className="text-muted-foreground text-sm">
                Manage growth levels for {department} department
              </p>
            </div>
          </div>
        </div>

        {/* Warning Alert */}
        <div
          className="flex items-center gap-3 p-4 rounded-lg border bg-amber-500/10"
          style={{ borderColor: 'hsl(var(--border))' }}
        >
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-foreground">
            <span className="font-medium">Warning:</span> Saved levels cannot be deleted! Be careful
            when adding new levels.
          </p>
        </div>

        {/* Levels Card */}
        <Card style={{ border: '1px solid hsl(var(--border))' }}>
          <CardContent className="p-6">
            <LevelsList
              levels={levels}
              isLoading={isLoading}
              onDragEnd={handleDragEnd}
              onRemove={handleRemoveLevel}
              onInput={handleInput}
              setHasError={setHasError}
            />

            {/* Actions */}
            <div
              className="flex items-center justify-between pt-6 mt-6 border-t"
              style={{ borderColor: 'hsl(var(--border))' }}
            >
              <Button variant="outline" onClick={handleAddLevel} disabled={isLoading}>
                <Plus className="h-4 w-4 mr-2" />
                Add Level
              </Button>

              <Button onClick={handleSave} disabled={isLoading || hasError || !hasChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Success Message Toast */}
      {message && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          {message}
          <button
            type="button"
            onClick={() => setMessage('')}
            className="font-bold hover:opacity-80"
          >
            &times;
          </button>
        </div>
      )}

      {/* Error Message Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          {error}
          <button type="button" onClick={() => setError('')} className="font-bold hover:opacity-80">
            &times;
          </button>
        </div>
      )}
    </ScrollArea>
  );
}

export default LevelsPageV3;
